import { lighten, darken, createTheme } from "@mui/material";

const warning = "#FFC260";
const error = "#FF0000";
const success = "#059965";
const info = "#0C7BDC";
const greyColor = "#757575";
const primaryColor = "#14375A";
const secondaryColor = "#CC7B29";
const blackColor = "#000000";
const lightenRate = 0.075;
const darkenRate = 0.18;

const defaultTheme = {
    palette: {
        primary: {
            light: "#ECEDED",
            main: primaryColor,
            dark: darken(primaryColor, darkenRate),
            contrastText: "#ECEDED",
        },
        secondary: {
            light: lighten(secondaryColor, lightenRate),
            main: secondaryColor,
            dark: darken(secondaryColor, darkenRate),
            contrastText: blackColor,
        },
        warning: {
            main: warning,
            light: lighten(warning, lightenRate),
            dark: darken(warning, darkenRate),
        },
        error: {
            main: error,
            light: lighten(error, lightenRate),
            dark: darken(error, darkenRate),
        },
        success: {
            main: success,
            light: lighten(success, lightenRate),
            dark: darken(success, darkenRate),
        },
        info: {
            main: info,
            light: lighten(info, lightenRate),
            dark: darken(info, darkenRate),
        },
        draft: {
            main: greyColor,
            light: lighten(greyColor, lightenRate),
            dark: darken(greyColor, darkenRate),
        },
        text: {
            primary: primaryColor,
            secondary: primaryColor,
        },
        background: {
            default: "#F6F7FF",
            light: "#F3F5FF",
        },
    },
    typography: {
        fontFamily: "Verdana, Arial, sans-serif",
        body1: { color: primaryColor },
        h1: { fontSize: "3rem", color: primaryColor },
        h2: { fontSize: "2rem", color: primaryColor },
        h3: { fontSize: "1.64rem", color: primaryColor },
        h4: { fontSize: "1.5rem", color: primaryColor },
        h6: { fontSize: "1rem", color: primaryColor },
        caption: { fontSize: "0.75rem", color: primaryColor }
    },
    customShadows: {
        widget: "0px 3px 11px 0px #E8EAFC, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A",
        widgetDark: "0px 3px 18px 0px #4558A3B3, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A",
        widgetWide: "0px 12px 33px 0px #E8EAFC, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A",
    },
    components: {
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    backgroundColor: "#4A4A4A1A",
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    boxShadow: "0px 3px 11px 0px #E8EAFC, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A",
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                icon: {
                    color: "#B9B9B9",
                },
                nativeInput: {
                    display: "none !important",
                }
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    "&.Mui-selected": {
                        backgroundColor: "#F3F5FF !important",
                        "&:focus": {
                            backgroundColor: "#F3F5FF",
                        },
                    },
                },
                button: {
                    "&:hover, &:focus": {
                        backgroundColor: "#F3F5FF",
                    },
                },
            },
        },
        MuiTouchRipple: {
            styleOverrides: {
                child: {
                    backgroundColor: "white",
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    height: 56,
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: "1px solid rgba(224, 224, 224, .5)",
                    paddingLeft: 24,
                },
                head: { fontSize: "0.95rem" },
                body: { fontSize: "0.95rem" },
            },
        },
        MuiAlert: {
            styleOverrides: {
                standardError: {
                    color: blackColor,
                    backgroundColor: 'transparent',
                    fontSize: '1rem',
                    '& .MuiAlert-icon': {
                        color: darken(error, darkenRate)
                    },
                },
                standardInfo: {
                    color: blackColor,
                    '& .MuiAlert-icon': {
                        color: blackColor
                    },
                },
                standardWarning: {
                    color: blackColor,
                    '& .MuiAlert-icon': {
                        color: blackColor
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                            borderColor: darken(error, darkenRate),
                        },
                    },
                    '& .MuiInputLabel-root.Mui-error': {
                        color: darken(error, darkenRate),
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    '&.Mui-error': {
                        color: darken(error, darkenRate),
                    },
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    '&.Mui-error': {
                        color: darken(error, darkenRate),
                    },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    '&.Mui-error': {
                        color: darken(error, darkenRate),
                    },
                },
            },
        },
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    '&:not(.Mui-completed):not(.Mui-active)': {
                        color: 'transparent',
                        border: '2px solid',
                        borderColor: primaryColor,
                        borderRadius: '50%',
                        '& .MuiStepIcon-text': {
                            fill: primaryColor,
                        }
                    }
                },
            },
        },
        MuiStepConnector: {
            styleOverrides: {
                line: {
                    borderColor: primaryColor,
                },
                root: {
                    '&.Mui-active .MuiStepConnector-line': {
                        borderColor: primaryColor,
                    },
                    '&.Mui-completed .MuiStepConnector-line': {
                        borderColor: primaryColor,
                    },
                },
            },
        },
        MuiDataGrid: {
            styleOverrides: {
                root: {
                    '& .MuiDataGrid-iconButtonContainer': {
                        visibility: 'visible !important',
                        width: 'auto !important',
                    },
                    '& .MuiDataGrid-sortIcon': {
                        opacity: '1 !important',
                        color: primaryColor,
                    },
                    '& .MuiDataGrid-columnHeader:focus': {
                        outline: 'none !important',
                        borderBottom: `3px solid ${primaryColor} !important`,
                    },
                    '& .MuiDataGrid-columnHeaderCheckbox:focus, & .MuiDataGrid-columnHeaderCheckbox:focus-within': {
                        outline: 'none !important',
                    },
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                clearIndicator: {
                    visibility: 'visible',
                },
            },
        },
    }
};

const themes = {
    default: createTheme(defaultTheme),
};

export default themes;